import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchJobs } from '../../api/jobs';
import StatusBadge from '../../components/common/StatusBadge';
import { Link } from 'react-router-dom';
import { Job } from '../../types/api';

// 計算工作進度（使用後端返回的統計數據）
const calculateProgress = (job: Job) => {
  // 優先使用後端計算的數據
  const totalStores = job.total_stores_count ?? job.stores.length;
  const completedStores = job.completed_stores_count ?? 0;
  
  if (totalStores === 0) return { completed: 0, total: 0, percentage: 0 };
  
  const percentage = Math.round((completedStores / totalStores) * 100);
  
  return { completed: completedStores, total: totalStores, percentage };
};

// 獲取顯示狀態
const getDisplayStatus = (job: Job) => {
  const { completed, total } = calculateProgress(job);
  
  // 如果所有店鋪都完成，顯示完成
  if (total > 0 && completed === total) {
    return 'completed';
  }
  
  // 如果有部分完成，顯示進行中
  if (completed > 0) {
    return 'in_progress';
  }
  
  // 否則使用原本的狀態
  return job.status;
};

// 進度條顏色
const getProgressColor = (percentage: number) => {
  if (percentage === 100) return '#10b981'; // 綠色 - 完成
  if (percentage >= 50) return '#3b82f6';   // 藍色 - 過半
  if (percentage > 0) return '#f59e0b';     // 黃色 - 開始了
  return '#d1d5db';                          // 灰色 - 未開始
};

const JobsListPage: React.FC = () => {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['jobs'],
    queryFn: () => fetchJobs(),
  });

  if (isLoading) {
    return (
      <div className="panel">
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <p>載入工作中...</p>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="panel">
        <p>載入工作列表時發生問題。</p>
        <button className="btn-primary" onClick={() => refetch()}>
          重新載入
        </button>
      </div>
    );
  }

  const jobs = data ?? [];

  return (
    <div className="panel">
      {jobs.length === 0 && (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
          <p>暫時沒有工作記錄。</p>
        </div>
      )}

      <div className="jobs-grid">
        {jobs.map((job) => {
          const progress = calculateProgress(job);
          const displayStatus = getDisplayStatus(job);
          const progressColor = getProgressColor(progress.percentage);
          
          return (
            <Link
              to={`/jobs/${job.id}`}
              key={job.id}
              className="job-card"
            >
              <div className="job-card-header">
                <div>
                  <h3>{job.job_name}</h3>
                </div>
                <StatusBadge status={displayStatus} />
              </div>
              
              {/* 進度區域 */}
              <div style={{ marginTop: '1rem' }}>
                {/* 進度條 */}
                <div style={{
                  width: '100%',
                  height: '12px',
                  backgroundColor: '#e5e7eb',
                  borderRadius: '6px',
                  overflow: 'hidden',
                  marginBottom: '0.75rem'
                }}>
                  <div style={{
                    width: `${progress.percentage}%`,
                    height: '100%',
                    backgroundColor: progressColor,
                    borderRadius: '6px',
                    transition: 'width 0.3s ease, background-color 0.3s ease'
                  }} />
                </div>
                
                {/* 進度文字 */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  fontSize: '0.875rem'
                }}>
                  <span style={{ color: '#6b7280' }}>
                    完成進度
                  </span>
                  <span style={{ 
                    fontWeight: 600,
                    color: progress.percentage === 100 ? '#10b981' : '#374151'
                  }}>
                    {progress.completed} / {progress.total} 間店鋪
                    {progress.percentage === 100 && (
                      <span style={{ marginLeft: '0.5rem', color: '#10b981' }}>✓</span>
                    )}
                  </span>
                </div>
                
                {/* 百分比 */}
                <div style={{
                  textAlign: 'center',
                  marginTop: '0.5rem',
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  color: progressColor
                }}>
                  {progress.percentage}%
                </div>
              </div>
              
              {/* 底部資訊 */}
              <p className="job-meta" style={{ marginTop: '1rem' }}>
                {progress.percentage === 100 ? (
                  <span style={{ color: '#10b981', fontWeight: 600 }}>
                    ✅ 所有店鋪已完成
                  </span>
                ) : progress.completed > 0 ? (
                  <span style={{ color: '#3b82f6' }}>
                    🔄 進行中，剩餘 {progress.total - progress.completed} 間
                  </span>
                ) : (
                  <span style={{ color: '#6b7280' }}>
                    ⏳ 尚未開始
                  </span>
                )}
              </p>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default JobsListPage;
